/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import moment from 'moment';
import {
	DefaultSplitDataRenderType,
	SplitData,
	isProjectTypeArchived,
	ProjectTypes,
	generateSimplestList,
	simpleLocalAttribute,
} from '@touchplan/common';

interface Priority {
	color: string;
	sortOrder: number;
	text: string;
}

interface Variance {
	varianceColor: string;
	varianceKey: number;
	varianceReason: string;
}

export interface ProjectFields {
	projectId?: string;
	projectName?: string;
	projectType?: string;
	accountId?: string;
	createdAt?: string;
	deletedAt?: string;
	enableP6Import?: boolean;
	fileAttachEnabled?: boolean;
	minDate?: string;
	maxData?: string;
	ownerUserId?: string;
	minX?: number;
	minY?: number;
	maxX?: number;
	maxY?: number;
	varianceCount?: number;
	workShiftsByDow?: number[];
	invoiceEndDate?: string;
	freeTrialStamp?: string;
	// FIXME ??  I don't think this "isDisabled" field is needed since whether a project
	// is "disabled" (a.k.a. "archived") depends mostly on the "projectType".
	// See "isProjectDisabled" in "project-types.ts".
	isDisabled?: boolean;
	useIntersectingPromisePeriods?: boolean;
	colors?: { [key: string]: any };
	users?: { [key: string]: boolean };
	priorities?: { [key: string]: Priority };
	variances?: { [key: string]: Variance };

	address?: string;
	city?: string;
	territory?: string;
	postcode?: string;
	timezoneId?: string;
	country?: string;
	marketSector?: string;
	salesforceProjectType?: string;
	notes?: string;
	defaultCrewSize?: number;
	wbsSeparator?: string;
	[key: string]: any; // leave this for any unknown props
}

export interface DeadlineData {
	date: string;
	fromNow: string; // E.g. "in 21 days"; currently not taken advantage of; potentially someday...
	diff: number;
	show?: any;
	paid?: any;
}

export class Project extends SplitData<ProjectFields, DefaultSplitDataRenderType> {
	protected attributeList = Object.assign(
		generateSimplestList([
			'projectId',
			'projectName',
			'projectLogo',
			'projectType',
			'deletedAt',
			'address',
			'city',
			'territory',
			'postcode',
			'timezoneId',
			'country',
			'marketSector',
			'salesforceProjectType',
			'notes',
			'wbsSeparator',
		]),
		{
			// project information dialog, local only
			costs: simpleLocalAttribute,
			schedules: simpleLocalAttribute,
		}
	);

	constructor(id: string) {
		super(id, DefaultSplitDataRenderType.database);
	}

	public getProjectType(renderType?: DefaultSplitDataRenderType) {
		const rt: string = this.defType(renderType);
		return this[rt].projectType as ProjectTypes;
	}

	public isDisabled(renderType?: DefaultSplitDataRenderType) {
		return this.getProjectType(renderType) === ProjectTypes.DISABLED;
	}

	public isArchived(renderType?: DefaultSplitDataRenderType) {
		const pt = this.getProjectType(renderType);
		return isProjectTypeArchived(pt);
	}

	public getProjectId(renderType?: DefaultSplitDataRenderType) {
		const rt: string = this.defType(renderType);
		return this[rt].projectId;
	}

	public getProcoreProjectId(renderType?: DefaultSplitDataRenderType) {
		const rt: string = this.defType(renderType);
		return this[rt].procoreProjectId;
	}

	public hasProcoreLink(renderType?: DefaultSplitDataRenderType) {
		return !!this.getProcoreProjectId(renderType);
	}

	public getFreeTrialStampData(renderType?: DefaultSplitDataRenderType): DeadlineData {
		return this.getDeadlineData('freeTrialStamp', renderType);
	}

	public getInvoiceData(renderType?: DefaultSplitDataRenderType): DeadlineData {
		return this.getDeadlineData('invoiceEndDate', renderType);
	}

	private getDeadlineData(fieldName: string, renderType?: DefaultSplitDataRenderType): DeadlineData {
		const rt: string = this.defType(renderType);
		const theDate = this[rt][fieldName];
		if (!theDate) {
			return null;
		}
		return {
			date: theDate,
			fromNow: moment(theDate).fromNow(),
			diff: moment(theDate).diff(moment().format('YYYY-MM-DD')),
		};
	}

	public getInvoicePaymentsData(
		dateThreshold: number,
		renderType?: DefaultSplitDataRenderType
	): DeadlineData {
		const rt: string = this.defType(renderType);
		const thePayment = this[rt]._currentPayment;
		if (!thePayment || !thePayment.date) {
			return null;
		}
		const date = thePayment.date;
		return {
			date,
			fromNow: moment(date).fromNow(true),
			diff: moment(date).diff(moment()),
			show: moment(date).diff(moment(), 'days') <= dateThreshold && !thePayment.paid,
			paid: thePayment.paid,
		};
	}

	public getCreatedAt(renderType?: DefaultSplitDataRenderType) {
		const rt: string = this.defType(renderType);
		return this[rt].createdAt as string;
	}

	public getOwnerUserId(renderType?: DefaultSplitDataRenderType) {
		const rt: string = this.defType(renderType);
		return this[rt].ownerUserId as string;
	}
}

// to avoid too much disruption for older code, remove later
export class ProjectLegacy {
	public $id?: string;
	public projectName?: string;
	public address?: string;
	public projectLogo?: any;
	public fileAttachEnabled?: boolean;
	public ownerUserId?: string;
	[index: string]: any;

	constructor(data, key?: string) {
		Object.assign(this, data);
		Object.assign(this, this.calcLegacyNag());
		if (key) {
			this.$id = key;
		}
	}

	//(legacy patch) calculate the "current payment" and nag
	//copied wholesale from angularjs projects.ts with minor edits
	private calcLegacyNag() {
		const updateObject: {
			_currentPayment?: { paid: boolean; date: string };
			overrideNagUntil?: string;
			_shouldNag?: boolean;
		} = {};

		if (this.invoicePayments) {
			var theOne;
			var theLast;
			Object.values(this.invoicePayments).forEach(function (payment) {
				if (!theOne && !payment['paid']) {
					theOne = payment;
				}
				theLast = payment;
			});
			if (!theOne) {
				theOne = theLast;
			}
			updateObject._currentPayment = theOne;

			if (
				!updateObject._currentPayment.paid &&
				moment(updateObject._currentPayment.date).isBefore(moment()) &&
				(!this.overrideNagUntil || moment(this.overrideNagUntil).isBefore(moment()))
			) {
				updateObject._shouldNag = true;
			} else {
				updateObject._shouldNag = false;
			}
		} else {
			updateObject._currentPayment = null;
		}

		return updateObject;
	}
}
